/** Срок списания подписки: сколько дней осталось и как это назвать.
 *
 *  Вынесено из экрана, чтобы проверялось тестом: экран тянет за собой Telegram
 *  и `window`, а правило про даты к ним отношения не имеет.
 */

/** День как YYYY-MM-DD по МЕСТНОМУ времени.
 *
 *  `toISOString()` отдаёт UTC, и во Вьетнаме (UTC+7) до семи утра он показывает
 *  вчера — семь часов каждых суток дата платежа по умолчанию была бы вчерашней,
 *  а сегодняшнюю `max` вообще не дал бы выбрать.
 */
export function isoDay(day: Date = new Date()): string {
  return `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}`
    + `-${String(day.getDate()).padStart(2, "0")}`;
}

/** Дней до списания: 0 — сегодня, отрицательное — просрочено. `null` — дата не разобралась. */
export function daysUntil(iso: string, today: Date = new Date()): number | null {
  const due = Date.parse(`${iso}T00:00:00`);
  if (Number.isNaN(due)) return null;
  // Полночь СВОЕГО дня, а не «сейчас минус сутки»: иначе в 23:00 завтрашняя
  // дата считалась бы сегодняшней.
  const midnight = Date.parse(`${isoDay(today)}T00:00:00`);
  return Math.round((due - midnight) / 86400000);
}

/** Сколько дней между двумя ISO-датами. `null` — дата не разобралась.
 *
 *  Нужно для честности прироста капитала: снимок — замер в точке месяца, и
 *  между 29 августа и 1 сентября «прирост» дала упавшая зарплата, а не
 *  накопление. Интервал, не похожий на месяц, надо называть вслух.
 */
export function daysBetween(fromIso: string, toIso: string): number | null {
  const from = Date.parse(`${fromIso}T00:00:00`);
  const to = Date.parse(`${toIso}T00:00:00`);
  if (Number.isNaN(from) || Number.isNaN(to)) return null;
  return Math.round((to - from) / 86400000);
}

/** Снимки сделаны в одной точке месяца? Только такие и сравнимы.
 *
 *  Считать дни бесполезно: между 1 октября и 5 ноября 35 дней — вроде месяц, —
 *  но первый замер был до ипотеки и второй зарплаты, а второй после. Совпадать
 *  должно ЧИСЛО месяца. Последний день месяца сравним с последним днём любого
 *  другого: 28 февраля и 31 марта — одна и та же точка цикла.
 */
export function sameCyclePoint(fromIso: string, toIso: string): boolean {
  const from = parts(fromIso);
  const to = parts(toIso);
  if (from === null || to === null) return false;
  if (from.day === to.day) return true;
  return isLastDay(from) && isLastDay(to);
}

type Ymd = { year: number; month: number; day: number };

function parts(iso: string): Ymd | null {
  const piece = iso.split("-").map(Number);
  if (piece.length !== 3 || piece.some((n) => !Number.isFinite(n))) return null;
  return { year: piece[0]!, month: piece[1]!, day: piece[2]! };
}

function isLastDay({ year, month, day }: Ymd): boolean {
  // Нулевой день следующего месяца — последний день этого.
  return new Date(year, month, 0).getDate() === day;
}

/** Подпись срока рядом с названием. `null` — подписи нет.
 *
 *  Просрочка раньше красилась как «завтра»: условие было «дней ≤ 1», а у
 *  прошедшей даты это число отрицательное, и ветка «ноль — сегодня, иначе —
 *  завтра» уводила её в будущее. Подписка от 2 ноября писала «завтра» пятого.
 */
export function dueBadge(days: number | null): string | null {
  if (days === null || days > 1) return null;
  if (days < 0) return "просрочено";
  return days === 0 ? "сегодня" : "завтра";
}
